/**
 * Capital-specific palette tokens.
 *
 * `primary`/`primaryDark` are aliased to the dashboard-wide primary so Capital
 * CTAs share the same purple as the rest of the app (Payments, Financing,
 * Reporting). The remaining tokens are Capital-specific surface tints
 * (lavender tile backgrounds for the sidebar promo and the amount card)
 * that don't have a close equivalent in the base palette — defining them
 * here keeps the values in one place rather than inlining hexes in components.
 *
 * Components should prefer base theme tokens (`palette.primary.main`,
 * `palette.text.primary`, `palette.background.paper`, `palette.divider`, etc.)
 * and only reach for `palette.capital.*` for the Capital-specific surface
 * tints that the base palette doesn't provide.
 */

import { Theme } from '@mui/material/styles'
import { lightPrimary, darkPrimary } from './colors'

export const lightCapital = {
	primary: lightPrimary.main,
	primaryDark: lightPrimary.dark,
	tileBackground: '#EEEDFE',
	tileBackgroundHover: '#E3E1FB',
	eyebrow: '#7B73C8',
	accentDeep: '#534AB7',
	surface: '#F7F5FC',
	disclosureBackground: '#F5F4FF'
}

export const darkCapital = {
	primary: darkPrimary.main,
	primaryDark: darkPrimary.dark,
	tileBackground: 'rgba(123, 128, 255, 0.16)',
	tileBackgroundHover: 'rgba(123, 128, 255, 0.24)',
	eyebrow: '#A29BFF',
	accentDeep: '#C7C2FF',
	surface: 'rgba(123, 128, 255, 0.08)',
	disclosureBackground: 'rgba(123, 128, 255, 0.06)'
}

/**
 * Map the active MUI theme onto the `appearance.variables` object Stripe
 * Connect consumes for embedded Capital components. Every color is pulled from
 * `theme.palette.*` (including `palette.capital.*`) so the Stripe embed
 * tracks theme edits without needing hex updates here.
 */
export const buildCapitalStripeAppearance = (theme: Theme): Record<string, string> => {
	const { palette, typography } = theme
	const isDark = palette.mode === 'dark'
	const capitalPrimary = palette.capital.primary

	const colorBackground = isDark ? palette.background.paper : palette.background.default
	const offsetBackground = isDark
		? ((palette.background as { tertiary?: string }).tertiary ?? '#2A2A3E')
		: palette.capital.disclosureBackground
	const formBackground = isDark
		? ((palette.background as { primary?: string }).primary ?? palette.background.paper)
		: palette.background.paper
	const mutedSecondary = isDark ? 'rgba(255, 255, 255, 0.55)' : palette.text.secondary

	return {
		colorPrimary: capitalPrimary,
		colorBackground,
		colorText: palette.text.primary,
		colorSecondaryText: mutedSecondary,
		colorDanger: palette.error.main,
		colorBorder: palette.divider,
		fontFamily: typography.fontFamily || 'inherit',
		borderRadius: '8px',
		offsetBackgroundColor: offsetBackground,
		formBackgroundColor: formBackground,
		formHighlightColorBorder: capitalPrimary,
		formAccentColor: capitalPrimary,
		overlayBackdropColor: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.4)',
		actionPrimaryColorText: capitalPrimary,
		actionSecondaryColorText: mutedSecondary,
		buttonPrimaryColorBackground: capitalPrimary,
		buttonPrimaryColorText: '#ffffff',
		buttonPrimaryColorBorder: capitalPrimary,
		buttonSecondaryColorBackground: offsetBackground,
		buttonSecondaryColorText: palette.text.primary,
		buttonSecondaryColorBorder: palette.divider
	}
}
